import React, { useState } from "react";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import Grid from "@material-ui/core/Grid";
import { Badge, Checkbox, FormControlLabel, IconButton, Input, useTheme } from "@material-ui/core";
import { useTable, useSortBy, useFilters, useGlobalFilter } from "react-table";
import Typography from "@material-ui/core/Typography";
import { TravelerModal } from "../../../../common/travelerModal";
import { Alert, AlertTitle } from "@material-ui/lab";
import { generalFilter, useDefaultColumn } from "../../../../../util/filter";
import CallSplitIcon from "@material-ui/icons/CallSplit";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { renderHeader } from "../../../../common/util";
import {
  AddCircle,
  CheckBox,
  CheckCircle,
  CheckCircleOutline,
  Edit,
  PriorityHigh,
  FileCopy
} from "@material-ui/icons";
import { TextField } from "formik-material-ui/dist/TextField";
import { Field } from "formik";
import { usePriority } from "../../../../dataEgress/dataEgressMutations";
import { toast } from "react-toastify";
import { useFetchContext } from "../../../../common/FetchContext";
import { useQueryClient } from "react-query";

export const CharacterizationTable = ({ data = [] }) => {
  const theme = useTheme();

  const { mutateAsync: mutatePriority } = usePriority();
  const { authAxios } = useFetchContext();
  const queryClient = useQueryClient();


  const defaultColumn = useDefaultColumn();
  const columns: any[] = React.useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
      },
      {
        id: "Serial Number",
        accessor: "serial_number",
        filter: generalFilter,

        Cell: ({ cell, row }: any) => {
          const { is_note, note_count } = row.original;
          const serialNumber = cell.value;

          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(String(serialNumber));
              toast.success("Serial number copied");
            } catch (error) {
              toast.error("Failed to copy serial number");
            }
          };

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
              }}
            >
              {is_note ? (
                <Badge badgeContent={note_count || 0} color="secondary">
                  <TravelerModal serialNumber={serialNumber} />
                </Badge>
              ) : (
                <TravelerModal serialNumber={serialNumber} />
              )}

              <IconButton
                size="small"
                onClick={handleCopy}
                title="Copy Serial Number"
              >
                <FileCopy fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      {
        id: "Test Sequence",
        accessor: "test_sequence",
        filter: generalFilter,
      },
      // {
      //   id: "Procedure Definition",
      //   accessor: "procedure_definition",
      //   filter: generalFilter,
      // },
      {
        id: "Customer",
        accessor: "customer",
        filter: generalFilter,
      },
      {
        id: "Project Number",
        accessor: "project_number",
        filter: generalFilter,
      },
      {
        id: "Work Order",
        accessor: "work_order",
        filter: generalFilter,
      },
      {
        id: "Location",
        accessor: "location",
        filter: generalFilter,
        Cell: ({ row }: any) => {
          return (
            <div>
              {row.values.Location ? row.values.Location : "N/A"}
            </div>
          );
        }

      },
      {
        id: "Execution Group Name",
        accessor: "characterization",
        filter: generalFilter,
      },
      {
        id: "Days since last action",
        accessor: "last_action_days",
        filter: generalFilter,
      },

      {
        id: "Priority",
        Header: "Priority",
        accessor: "priority_flag",

        filter: generalFilter,

        Cell: ({ row }: any) => {
          const { priority_flag, procedure_result_id } = row.original;
          const [isChecked, setIsChecked] = useState(priority_flag === 1);

          const handleToggle = async () => {
            const newStatus = !isChecked;
            setIsChecked(newStatus);

            try {
              await mutatePriority({
                authAxios,
                procedure_result_id,
                status: newStatus,
              });

              toast.success("Priority updated");
              queryClient.invalidateQueries("characterizationQueue");

            } catch (e) {
              toast.error("Error updating priority");
              setIsChecked(!newStatus);
            }
          };

          return (
            <div>
              <Checkbox
                icon={<AddCircle />}
                checkedIcon={<CheckCircle />}
                name="priority"
                checked={isChecked}
                onChange={handleToggle}
              />
            </div>
          );
        },
      }


    ],
    []
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      data,
      columns,
      defaultColumn,
      initialState: {
        hiddenColumns: ["id", 'priority_flag'],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  return (
    <Grid container direction="row" justify="space-between" alignItems="center">
      {!data.length && (
        <Alert severity="info">
          <AlertTitle>Info</AlertTitle>
          <strong>No procedures Available</strong>
        </Alert>
      )}
      {data.length > 0 && (
        <>
          <Grid item xs={12}>
            <TableContainer
              data-testid="tableContainer"
              style={theme.tableContainer}
            >
              <MuiTable {...getTableProps()}>
                <TableHead>
                  {headerGroups.map((headerGroup) => (
                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <TableCell style={theme.noLeftRightPadding}>
                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            spacing={0}
                          >
                            <Grid item xs={12} {...column.getHeaderProps()}>
                              <Grid
                                container
                                direction="row"
                                alignItems="center"
                                spacing={0}
                              >
                                <Typography variant="subtitle2">
                                  {renderHeader(column)}
                                </Typography>
                                {column.canFilter
                                  ? column.render("Filter")
                                  : null}
                                <Grid item xs={2}>
                                  <IconButton
                                    type="submit"
                                    aria-label="search"
                                    size="small"
                                    {...column.getSortByToggleProps()}
                                  >
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <ArrowDropDownIcon />
                                      ) : (
                                        <ArrowDropUpIcon />
                                      )
                                    ) : (
                                      <CallSplitIcon />
                                    )}
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <TableRow {...row.getRowProps()} style={{
                        backgroundColor: (row.original as any).is_note ? "#6db4f3" : "inherit",
                      }}>
                        {row.cells.map((cell) => {
                          return (
                            <TableCell {...cell.getCellProps()}>
                              {cell.render("Cell")}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Grid>
        </>
      )}
    </Grid>
  );
};
